import React from 'react'
import { Box, Flex } from '@chakra-ui/core'
import { css } from '@emotion/core'
import {
  CameraIcon,
  LinkIcon,
  PollIcon,
  VideoIcon,
  QuestionIcon,
  DescriptionIcon,
  PrivatePollIcon,
} from 'components/CommonComponents/JSXIcons'
import keys from 'constants/keys'
import { Content, OpenQuestionSubtask, PollSubtask, StepType, Subtask, useFormStepContext } from '../FormStepContext'
import { CustomTooltip } from 'components/helperStyles'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'

const SUBTASK_BUTTONS = [
  {
    type: keys.SUBTASK_TYPES.DESCRIPTION,
    label: 't_desc',
    ariaLabel: 'Add description',
    Component: DescriptionIcon,
  },
  {
    type: keys.SUBTASK_TYPES.POLL_SUBTASK,
    label: 't_st_poll',
    ariaLabel: 'Add poll',
    Component: PollIcon,
  },
  {
    type: keys.SUBTASK_TYPES.PRIVATE_POLL,
    label: 't_st_quiz',
    ariaLabel: 'Add private poll',
    Component: PrivatePollIcon,
  },
  {
    type: keys.SUBTASK_TYPES.LINK_SUBTASK,
    label: 't_st_link',
    ariaLabel: 'Add link',
    Component: LinkIcon,
  },
  {
    type: keys.SUBTASK_TYPES.IMAGE_SUBTASK,
    label: 't_st_image',
    ariaLabel: 'Add image',
    Component: CameraIcon,
  },
  {
    type: keys.SUBTASK_TYPES.VIDEO_SUBTASKS,
    label: 't_st_video',
    ariaLabel: 'Add video',
    Component: VideoIcon,
  },
  {
    type: keys.SUBTASK_TYPES.OPEN_QUESTION,
    label: 't_st_open_qst',
    ariaLabel: 'Add question',
    Component: QuestionIcon,
  },
]

export const Footer = () => {
  const { subtaskActions, stepStack, isEdit } = useFormStepContext()
  const { translations, locale } = useSelector((state: RootState) => state.config)

  const iconHoverEffect = css`
    display: flex;
    align-items: center;
    column-gap: 30px;

    & > * {
      transition: transform 0.3s ease, margin 0.3s ease;
    }

    & > *:hover {
      transform: scale(1.2);
    }
  `

  const tooltipStyles = css`
    .chakra-tooltip {
      z-index: 999999 !important;
    }
    .chakra-tooltip__content {
      background: #2d3748;
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 14px;
    }
  `

  const handleAddSubtask = (type: number) => {
    switch (type) {
      case keys.SUBTASK_TYPES.POLL_SUBTASK:
        subtaskActions.append?.({
          title: '',
          is_required: true,
          is_multi_choice: false,
          type: keys.SUBTASK_TYPES.POLL_SUBTASK,
          options: ['', ''],
        } as PollSubtask)
        break
      case keys.SUBTASK_TYPES.PRIVATE_POLL:
        subtaskActions.append?.({
          title: '',
          is_required: true,
          type: keys.SUBTASK_TYPES.PRIVATE_POLL,
        } as PollSubtask)
        break
      case keys.SUBTASK_TYPES.IMAGE_SUBTASK:
        subtaskActions.append?.({
          title: '',
          is_required: true,
          type: keys.SUBTASK_TYPES.IMAGE_SUBTASK,
        } as Subtask)
        break
      case keys.SUBTASK_TYPES.VIDEO_SUBTASKS:
        subtaskActions.append?.({
          title: '',
          is_required: true,
          type: keys.SUBTASK_TYPES.VIDEO_SUBTASKS,
        } as Subtask)
        break
      case keys.SUBTASK_TYPES.LINK_SUBTASK:
        subtaskActions.append?.({
          title: '',
          is_required: true,
          type: keys.SUBTASK_TYPES.LINK_SUBTASK,
          external_link: '',
          is_external_status_update: true,
        } as Subtask)
        break
      case keys.SUBTASK_TYPES.OPEN_QUESTION:
        subtaskActions.append?.({
          title: '',
          is_required: true,
          is_numeric: false,
          type: keys.SUBTASK_TYPES.OPEN_QUESTION,
        } as OpenQuestionSubtask)
        break
      case keys.SUBTASK_TYPES.DESCRIPTION:
        subtaskActions.append?.({
          type: keys.SUBTASK_TYPES.DESCRIPTION,
          text: '',
          images: [],
          videos: [],
          files: [],
          index_ref: 0,
        } as Content)
        break
      default:
        break
    }
  }

  return (
    <>
      {stepStack[stepStack.length - 1] === StepType.SUBTASK_TASK_CREATION && !isEdit ? (
        <Flex css={[iconHoverEffect, tooltipStyles]} bg="transparent">
          {SUBTASK_BUTTONS.map(({ type, Component, label, ariaLabel }) => {
            const t_label = (translations[locale][label] ?? label) as string
            return (
              <CustomTooltip key={type} label={t_label} aria-label={ariaLabel} placement="bottom">
                <Box key={type} onClick={() => handleAddSubtask(type)}>
                  <Component cursor="pointer" />
                </Box>
              </CustomTooltip>
            )
          })}
        </Flex>
      ) : (
        <Box padding="20px" />
      )}
    </>
  )
}
